import { type ItineraryLegDTO } from "../types/dtos/api/itinerary/itinerary.dto";
import { LegType } from "../types/itinerary/leg-type.enum";
import { type Mode } from "../types/ItineraryPlanner";

/** Minimal itinerary leg fields used for transport mode / rental UI grouping. */
export type ItineraryLegTransportSignals = Pick<ItineraryLegDTO, "mode" | "type" | "rental">;

export enum TransportModeGroup {
    "MY_CAR" = "MY_CAR",
    "CAR" = "CAR",
    "TRAIN" = "TRAIN",
    "TRAM" = "TRAM",
    "BUS" = "BUS",
    "BOAT" = "BOAT",
    "WALK" = "WALK",
    "BICYCLE" = "BICYCLE",
    "RENTAL" = "RENTAL",
    "BLABLADAILY" = "BLABLADAILY",
    "BLABLACAR" = "BLABLACAR",
    "HE_LEMAN" = "HE_LEMAN",
    "UNSUPPORTED_LIFT" = "UNSUPPORTED_LIFT",
    "UNKNOWN" = "UNKNOWN",
}

export enum RentalVehicleDisplayGroup {
    "BICYCLE" = "BICYCLE",
    "CARGO_BICYCLE" = "CARGO_BICYCLE",
    "SCOOTER_STANDING" = "SCOOTER_STANDING",
    "SCOOTER_SEATED" = "SCOOTER_SEATED",
    "CAR" = "CAR",
    "MOPED" = "MOPED",
    "OTHER" = "OTHER",
}

export type TransportModeLike =
  | Mode |
  "transit" |
  "metro" |
  "ferry" |
  "gondola" |
  "highspeed_rail" |
  "long_distance" |
  "regional_rail" |
  "regional_fast_rail" |
  "night_rail" |
  "regional_express_rail" |
  "suburban" |
  "aerial_lift" |
  "cable_car" |
  "areal_lift" |
  "trolleybus" |
  "monorail" |
  "funicular" |
  "intercity" |
  "public_transport";

const normalizeMode = ( mode: string ): string => mode.trim().toLowerCase();

/**
 * Maps GTFS `route_type` (standard and common extended values) to a {@link TransportModeGroup} for UI (icons, labels).
 *
 * @param routeType Numeric `route_type` from GTFS `routes.txt` (or API echo).
 * @returns Group used for presentation; unknown values fall back to {@link TransportModeGroup.BUS}.
 * @see https://developers.google.com/transit/gtfs/reference#routestxt
 */
export const getTransportModeGroupFromGtfsRouteType = ( routeType: number | null ): TransportModeGroup => {
    switch ( routeType ) {
        case 0: // Fallthrough

        case 900:
            return TransportModeGroup.TRAM;

        case 2: // Fallthrough

        case 100: // Fallthrough

        case 101: // Fallthrough

        case 102: // Fallthrough

        case 103: // Fallthrough

        case 106: // Fallthrough

        case 109:
            return TransportModeGroup.TRAIN;

        case 3: // Fallthrough

        case 700: // Fallthrough

        case 701: // Fallthrough

        case 702: // Fallthrough

        case 704: // Fallthrough

        case 715:
            return TransportModeGroup.BUS;

        case 4: // Fallthrough

        case 1000: // Fallthrough

            return TransportModeGroup.BOAT;

        default:
            return TransportModeGroup.BUS;
    }
};

export const isGtfsRouteTypeTrainLike = ( routeType: number | null ): boolean => {
    return getTransportModeGroupFromGtfsRouteType( routeType ) === TransportModeGroup.TRAIN;
};

export const getTransportModeGroup = ( mode: string | null | undefined ): TransportModeGroup => {
    if ( mode == null ) {
        return TransportModeGroup.UNKNOWN;
    }

    switch ( normalizeMode( mode ) ) {
        case "mycar":
            return TransportModeGroup.MY_CAR;

        case "car":
            return TransportModeGroup.CAR;

        case "transit": // Fallthrough

        case "public_transport":
            // Generic multimodal label from providers: treat as fixed-guideway / metro-style for UI coloring.
            return TransportModeGroup.TRAM;

        case "train": // Fallthrough

        case "highspeed_rail": // Fallthrough

        case "long_distance": // Fallthrough

        case "regional_rail": // Fallthrough

        case "regional_fast_rail": // Fallthrough

        case "night_rail": // Fallthrough

        case "regional_express_rail": // Fallthrough

        case "suburban": // Fallthrough

        case "intercity": // Fallthrough

        case "rail":
            return TransportModeGroup.TRAIN;

        case "tram": // Fallthrough

        case "metro": // Fallthrough

        case "subway":
            return TransportModeGroup.TRAM;

        case "bus": // Fallthrough

        case "coach": // Fallthrough

        case "trolley": // Fallthrough

        case "trolleybus": // Fallthrough

        case "odm":
            return TransportModeGroup.BUS;

        case "ferry": // Fallthrough

        case "boat":
            return TransportModeGroup.BOAT;

        case "monorail": // Fallthrough

        case "funicular":
            return TransportModeGroup.TRAM;

        case "walk":
            return TransportModeGroup.WALK;

        case "bicycle":
            return TransportModeGroup.BICYCLE;

        case "rental":
            return TransportModeGroup.RENTAL;

        case "blabladaily":
            return TransportModeGroup.BLABLADAILY;

        case "blablacar":
            return TransportModeGroup.BLABLACAR;

        case "heleman":
            return TransportModeGroup.HE_LEMAN;

        case "gondola": // Fallthrough

        case "aerial_lift": // Fallthrough

        case "cable_car": // Fallthrough

        case "areal_lift":
            return TransportModeGroup.UNSUPPORTED_LIFT;

        default:
            return TransportModeGroup.UNKNOWN;
    }
};

export const isTransportModeInGroups = (
    mode: string | null | undefined,
    groups: readonly TransportModeGroup[],
): boolean => groups.includes( getTransportModeGroup( mode ) );

export const isDurationOnlyTransportMode = ( mode: string | null | undefined ): boolean => {
    const group = getTransportModeGroup( mode );

    return group === TransportModeGroup.WALK ||
      group === TransportModeGroup.BICYCLE ||
      group === TransportModeGroup.RENTAL ||
      group === TransportModeGroup.BLABLADAILY ||
      group === TransportModeGroup.BLABLACAR;
};

export const isTransitLikeTransportMode = ( mode: string | null | undefined ): boolean => {
    const group = getTransportModeGroup( mode );

    return group === TransportModeGroup.BUS ||
      group === TransportModeGroup.TRAIN ||
      group === TransportModeGroup.TRAM ||
      group === TransportModeGroup.HE_LEMAN ||
      group === TransportModeGroup.BLABLACAR ||
      normalizeMode( mode ?? "" ) === "gondola";
};

export const mapTransportModeToRouteType = ( mode: string | null | undefined ): "car" | "walk" | "transit" | "bike" | "other" => {
    const group = getTransportModeGroup( mode );

    if ( group === TransportModeGroup.CAR || group === TransportModeGroup.MY_CAR || group === TransportModeGroup.HE_LEMAN ) {
        return "car";
    }

    if ( group === TransportModeGroup.WALK ) {
        return "walk";
    }

    if ( group === TransportModeGroup.BUS || group === TransportModeGroup.TRAIN || group === TransportModeGroup.TRAM ) {
        return "transit";
    }

    if ( group === TransportModeGroup.BICYCLE || group === TransportModeGroup.RENTAL ) {
        return "bike";
    }

    return "other";
};

/**
 * Whether the leg is a carpool partner segment (BlaBlaCar / BlaBlaDaily).
 * These legs carry `rental` metadata for pricing and booking, but must not use rental micromobility UI.
 *
 * @param leg Itinerary leg transport signals.
 * @returns True when {@link LegType} denotes a carpool provider leg.
 */
export const isCarpoolPartnerItineraryLeg = ( leg: ItineraryLegTransportSignals ): boolean => {
    return leg.type === LegType.blablacar || leg.type === LegType.blabladaily;
};

/**
 * Whether an itinerary leg represents shared mobility (rental).
 * Uses API `mode` (`rental`), canonical `type` (`rentalMotis`), and `rental` metadata.
 * Carpool partner legs are excluded even when `rental.vehicle.type` is `CAR`.
 *
 * @param leg Itinerary leg transport signals.
 * @returns True when the leg should use rental UI grouping and colors.
 */
export const isSharedMobilityItineraryLeg = ( leg: ItineraryLegTransportSignals ): boolean => {
    if ( isCarpoolPartnerItineraryLeg( leg ) ) {
        return false;
    }

    if ( leg.type === LegType.rentalMotis ) {
        return true;
    }

    if ( leg.rental != null ) {
        return true;
    }

    return getTransportModeGroup( leg.mode ) === TransportModeGroup.RENTAL;
};

/**
 * Resolves the transport-mode token used for palette lookup on shared-mobility legs.
 * Car-sharing rentals use the car palette; other form factors keep the itinerary leg mode.
 *
 * @param leg Itinerary leg transport signals.
 * @returns Mode string to pass to {@link getTransportModeGroup} / color helpers.
 */
export const getRentalLegColorMode = ( leg: ItineraryLegTransportSignals ): string => {
    if ( isCarpoolPartnerItineraryLeg( leg ) ) {
        return leg.mode ?? "";
    }

    if ( !isSharedMobilityItineraryLeg( leg ) ) {
        return leg.mode ?? "";
    }

    const rentalVehicleType = leg.rental?.vehicle?.type;

    if ( getRentalVehicleDisplayGroup( rentalVehicleType ) === RentalVehicleDisplayGroup.CAR ) {
        return "car";
    }

    return leg.mode ?? "rental";
};

/**
 * Transport mode group for itinerary UI from leg mode, type, and rental metadata.
 * Carpool {@link LegType} is resolved before shared-mobility rental heuristics so partner branding stays intact.
 *
 * @param leg Itinerary leg transport signals.
 * @returns Group for icons, colors, and detail panels.
 */
export const getTransportModeGroupForItineraryLeg = ( leg: ItineraryLegTransportSignals ): TransportModeGroup => {
    if ( leg.type === LegType.blablacar ) {
        return TransportModeGroup.BLABLACAR;
    }

    if ( leg.type === LegType.blabladaily ) {
        return TransportModeGroup.BLABLADAILY;
    }

    if ( isSharedMobilityItineraryLeg( leg ) ) {
        return TransportModeGroup.RENTAL;
    }

    return getTransportModeGroup( leg.mode );
};

export const getRentalVehicleDisplayGroup = ( rentalVehicleType: string | undefined ): RentalVehicleDisplayGroup => {
    if ( rentalVehicleType === undefined ) {
        return RentalVehicleDisplayGroup.OTHER;
    }

    if ( rentalVehicleType === "BICYCLE" ) {
        return RentalVehicleDisplayGroup.BICYCLE;
    }

    if ( rentalVehicleType === "CARGO_BICYCLE" ) {
        return RentalVehicleDisplayGroup.CARGO_BICYCLE;
    }

    if ( rentalVehicleType === "SCOOTER_STANDING" ) {
        return RentalVehicleDisplayGroup.SCOOTER_STANDING;
    }

    if ( rentalVehicleType === "SCOOTER_SEATED" ) {
        return RentalVehicleDisplayGroup.SCOOTER_SEATED;
    }

    if ( rentalVehicleType === "CAR" ) {
        return RentalVehicleDisplayGroup.CAR;
    }

    if ( rentalVehicleType === "MOPED" ) {
        return RentalVehicleDisplayGroup.MOPED;
    }

    return RentalVehicleDisplayGroup.OTHER;
};
